import styled from '@emotion/styled';

import { Grid, Card, CardHeader, CardContent } from '@mui/material';

import { CardBlockContainer, CardBlock, CardBlockHeader, CardSpacer } from '../../../utils/utilities/CardComponents';

import MultiStatusChip from '../../../utils/utilities/MultiStatusChip';

import SimpleChip from '../../../utils/utilities/SimpleChip';

import { relativeTime, formatPhoneNumber } from '../../../utils/util';

import PrepStage from '../Components/PrepStage';

import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import AccountBoxOutlinedIcon from '@mui/icons-material/AccountBoxOutlined';
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import SettingsSuggestRoundedIcon from '@mui/icons-material/SettingsSuggestRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import UpdateRoundedIcon from '@mui/icons-material/UpdateRounded';
import ListAltRoundedIcon from '@mui/icons-material/ListAltRounded';
import PinDropRoundedIcon from '@mui/icons-material/PinDropRounded';
import ArchiveRoundedIcon from '@mui/icons-material/ArchiveRounded';
import NextPlanRoundedIcon from '@mui/icons-material/NextPlanRounded';
import PhoneAndroidRoundedIcon from '@mui/icons-material/PhoneAndroidRounded';

import TimelineWithComments from '../../../utils/utilities/Timeline/TimelineWithComments';

import { BasicFlex, Notes } from '../../../utils/Common';

import EditStages from '../Components/EditStages';

// use pageData context
import { usePageData } from '../../../contexts/PageData';

const StageListHeader = styled.div`
  margin: 2rem 0;
  background-color: ${props => props.theme.colors.lightBg};
  padding: .5rem 1rem;
  border-radius: 5px;
  border: 1px solid ${props => props.theme.colors.border};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
` 

const StageList = styled(BasicFlex)`
  margin-top: 2rem;
`

const ServiceTab = () => {
  const { data = {} } = usePageData();

  const basicBlock = [
    {header: "Ticket ID", icon: <ConfirmationNumberRoundedIcon fontSize='small' color="basic" />, info: `#${data.service_ticket_id}`},
    {header: "Wig ID", icon: <LocalOfferRoundedIcon fontSize='small' color="basic" />, info: data.wig_id},
    {header: "Service", icon: <SettingsSuggestRoundedIcon fontSize='small' color="basic" />, info: <SimpleChip label={data.service_name} />},
    {header: "Scheduled Check In", info: relativeTime(data.scheduled_check_in_at)},
  ]

  const apptBlock = [
    {header: "Date", icon: <EventRoundedIcon fontSize='small' color="basic" />, info: data.scheduled_date, dataType: "date"},
    {header: "Duration", icon: <UpdateRoundedIcon fontSize='small' color="basic" />, info: `${data.duration_in_minutes} Min`},
  ]

  const clientBlock = [
    {header: "Customer", icon: <AccountBoxOutlinedIcon fontSize='small' />, info: data.customer_name},
    {header: "Phone", icon: <PhoneAndroidRoundedIcon fontSize='small' />, info: data.customer_phone, dataType: "phone"}
  ];

  const stylistBlock = [
    {header: "Stylist", icon: <AccountBoxOutlinedIcon fontSize='small' />, info: data.stylist},
  ];

  let curStation = data?.prep_stages?.findIndex(stage => stage.name === data.current_station);

  const currentStageBlock = data.current_station ? [
    {header: "Current Station", icon: <PinDropRoundedIcon fontSize='small' color="basic" />, info: `${data.current_station} Station`},
    {header: "Received", icon: <ArchiveRoundedIcon fontSize='small' color="basic" />, info: relativeTime(data.prep_stages[curStation].received_at)},
  ] : [];

  if ( curStation !== -1 && data?.prep_stages[curStation + 1]) {
    currentStageBlock.push({header: "Next Station", icon: <NextPlanRoundedIcon fontSize='small' color="basic" />, info: data.prep_stages[curStation + 1].name});
  }
 
  // const activityBlock = [
  //   {header: "Active", info: data.is_active, dataType: "boolean", booleanValues: ["Active", "Inactive"]},
  //   {header: "Date Created", info: data.created_at, dataType: "date"},
  // ];

  return (
    <Grid container spacing={3}>
      <Grid size={{xs: 12, md: 4}}>
        <Card variant='outlined'>
          <CardHeader title="Service Details" action={<MultiStatusChip value={data.status} values={[{value: "scheduled", label: "Scheduled", status: "primary"}, {value: "checked_in", label: "Checked In", status: "secondary"}, {value: "prepping", label: "In Prep", status: "warning"}, {value: "in_progress", label: "In Progress", status: "warning"}, {value: "completed", status: "success"}]} />} />
          <CardContent>
            <CardBlockContainer>
              {basicBlock.map((block) => (
                <CardBlock key={block.header} {...block} />
              ))}

              <CardSpacer />
              <CardBlockHeader>Appointment Information</CardBlockHeader>
              {apptBlock.map((block) => (
                <CardBlock key={block.header} {...block} />
              ))}

              <CardSpacer />
              <CardBlockHeader>Customer Information</CardBlockHeader>
              {clientBlock.map((block) => (
                <CardBlock key={block.header} {...block} />
              ))}
              <CardSpacer />

              <CardBlockHeader>Stylist Information</CardBlockHeader>
              {stylistBlock.map((block) => (
                <CardBlock key={block.header} {...block} />
              ))}
        
            </CardBlockContainer>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{xs: 12, md: 8}}>
        <Card variant='outlined'>
          <CardHeader title={<BasicFlex gap="1rem">Service Ticket History <HistoryRoundedIcon /></BasicFlex>} />
          <CardContent>
            <TimelineWithComments resource="service_tickets" resource_id={data.service_ticket_id} />
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{xs: 12, md: 4}}>
        <Card variant='outlined'>
          <CardHeader title={<BasicFlex gap="1rem">Service Preparation Stages<ListAltRoundedIcon /></BasicFlex>} />
          <CardContent>
            <CardBlockContainer>
              <CardBlockHeader>Preparation Status</CardBlockHeader>
              {currentStageBlock.map((block) => (
                <CardBlock key={block.header} {...block} />
              ))}
            </CardBlockContainer>
            <StageListHeader>
              <CardBlockHeader>List of Stages</CardBlockHeader>
              <EditStages stages={data?.prep_stages} ticket={data} />
            </StageListHeader>
            <StageList dir="column" gap="1.5rem">
              {data.prep_stages.map((stage, index) => (
                <PrepStage key={index} stage={stage} />
              ))}
            </StageList>
          </CardContent>
        </Card>
      </Grid>
 
    </Grid>
  );

};

export default ServiceTab;